import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, getAuth, formatINR } from "@/lib/api";
import { toast } from "sonner";
import { formatDay, formatMoment } from "@/lib/slots";

const statusColors = {
  pending: { bg: "#FEF3C7", color: "#854D0E" },
  confirmed: { bg: "#DBEAFE", color: "#1E3A8A" },
  assigned: { bg: "#E0E7FF", color: "#3730A3" },
  in_progress: { bg: "#FCE7F3", color: "#9D174D" },
  completed: { bg: "#DCFCE7", color: "#166534" },
  cancelled: { bg: "#FEE2E2", color: "#991B1B" },
  rejected: { bg: "#FEE2E2", color: "#991B1B" },
  rescheduled: { bg: "#FEF3C7", color: "#854D0E" },
};

export default function TenantBookings() {
  const { slug } = useParams();
  const nav = useNavigate();
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/customer/${slug}/bookings`);
      setBookings(r.data);
    } catch (e) {
      setBookings([]);
      // Signed out, or the session is not for this business: sign in here.
      if ([401, 403].includes(e?.response?.status)) nav(`/t/${slug}/login?next=/t/${slug}/bookings`);
    }
    setLoading(false);
  };

  useEffect(() => {
    const auth = getAuth();
    if (!auth || auth.user?.role !== "customer") {
      nav(`/t/${slug}/login?next=/t/${slug}/bookings`);
      return;
    }
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]);

  const cancel = async (id) => {
    if (!window.confirm("Cancel this booking?")) return;
    try {
      await api.post(`/customer/${slug}/bookings/${id}/cancel`);
      toast.success("Cancelled");
    } catch (e) {
      // A job that has already started (or finished) cannot be cancelled here.
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "The booking could not be cancelled");
    }
    load();
  };

  return (
    <div className="px-5 py-5 md:px-6 md:py-8" data-testid="tenant-bookings-page">
      <h1 className="brand-heading font-bold text-2xl md:text-3xl">My bookings</h1>
      {loading && <div className="text-sm brand-text-muted mt-5">Loading…</div>}
      {!loading && bookings.length === 0 && <div className="text-sm brand-text-muted text-center py-10">No bookings yet.</div>}
      <div className="mt-5 grid gap-3 md:grid-cols-2 xl:grid-cols-3 md:gap-4">
        {bookings.map((b) => {
          const sc = statusColors[b.status] || statusColors.pending;
          return (
            <div key={b.id} className="brand-card p-4 min-w-0" data-testid={`tenant-booking-${b.id}`}>
              <div className="flex justify-between items-start gap-3">
                <div className="min-w-0">
                  <div className="text-xs font-mono brand-text-muted">{b.code}</div>
                  <div className="font-bold mt-1 break-words">{b.service_name}</div>
                </div>
                <span className="status-pill flex-shrink-0" style={{ background: sc.bg, color: sc.color }}>{b.status.replace("_", " ")}</span>
              </div>
              <div className="mt-3 grid grid-cols-2 gap-2 text-xs brand-text-muted">
                <div>📅 {formatDay(b.scheduled_date)}</div>
                <div>⏰ {b.scheduled_slot}</div>
                <div className="break-words min-w-0">👤 {b.seller_name || "Auto-assign"}</div>
                <div className="font-bold brand-text">{formatINR(b.total)}</div>
              </div>
              {(b.started_at || b.completed_at) && (
                <div className="mt-3 pt-3 border-t brand-border text-xs brand-text-muted space-y-0.5" data-testid={`tenant-booking-times-${b.id}`}>
                  {b.started_at && <div>Work started: <span className="brand-text font-semibold">{formatMoment(b.started_at)}</span></div>}
                  {b.completed_at && <div>Completed: <span className="brand-text font-semibold">{formatMoment(b.completed_at)}</span></div>}
                </div>
              )}
              {["pending", "confirmed", "assigned", "rescheduled"].includes(b.status) && (
                <button onClick={() => cancel(b.id)} className="mt-3 w-full brand-muted brand-text py-3 rounded-full text-xs font-semibold" data-testid={`tenant-booking-cancel-${b.id}`}>
                  Cancel booking
                </button>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
